/* One quiet scheduler across all counters. No polling, cart reads, or mutation
   observers; confirmed CartState snapshots own the number. */
(() => {
  if (customElements.get('cart-counter')) return;
  const counters = new Set();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  let timer, owner, pass = 0;
  const visible = counter => {
    const r = counter.getBoundingClientRect();
    return counter.isConnected && r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth && counter.checkVisibility({ checkVisibilityCSS: true, checkOpacity: true });
  };
  function schedule(first = false) {
    clearTimeout(timer);
    if (!counters.size || reduced.matches || document.hidden) return;
    timer = setTimeout(() => {
      const choices = [...counters].filter(counter => counter.dataset.glow === 'true' && visible(counter));
      if (!owner && choices.length) {
        // Red is a single flare, once in nine passes; never a strobe.
        const effect = ++pass % 9 === 0 ? 'red' : pass % 2 ? 'gold' : 'rainbow';
        choices[Math.floor(Math.random() * choices.length)].ignite(effect);
      }
      schedule();
    }, first ? 8000 + Math.random() * 5000 : 18000 + Math.random() * 14000);
  }
  function suspend() {
    clearTimeout(timer);
    counters.forEach(counter => counter.extinguish(true));
    schedule();
  }
  reduced.addEventListener('change', suspend);
  fine.addEventListener('change', suspend);
  document.addEventListener('visibilitychange', suspend);
  window.addEventListener('pagehide', () => { clearTimeout(timer); counters.forEach(counter => counter.extinguish(true)); });
  window.addEventListener('pageshow', () => schedule(true));
  class CartCounter extends HTMLElement {
    connectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = new AbortController();
      const options = { signal: this.lifecycle.signal };
      const count = window.ThemeCart?.cart?.item_count;
      if (count !== undefined) this.setCount(count);
      counters.add(this);
      if (counters.size === 1) schedule(true);
      const link = this.closest('a,button') || this;
      link.addEventListener('pointerenter', () => {
        if (this.dataset.hoverGlow === 'true' && fine.matches) this.ignite(this.dataset.hoverEffect, true);
      }, options);
      link.addEventListener('pointerleave', () => this.extinguish(), options);
      link.addEventListener('pointerdown', () => this.extinguish(true), options);
      link.addEventListener('keydown', () => this.extinguish(true), options);
      this.visibility = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) this.extinguish(true);
      });
      this.visibility.observe(this);
    }
    setCount(count) {
      const value = this.querySelector('[data-counter-value]');
      if (!value) return;
      const text = String(count);
      if (value.textContent !== text) value.textContent = text;
      if (this.dataset.cartCount !== text) this.dataset.cartCount = text;
    }
    ignite(effect, hover = false) {
      if (reduced.matches || document.hidden || !visible(this)) return;
      if (!['gold', 'rainbow', 'red'].includes(effect)) effect = 'gold';
      owner?.extinguish(true);
      clearTimeout(this.release);
      owner = this;
      this.dataset.counterEffect = effect;
      this.toggleAttribute('data-counter-hovering', hover);
      this.setAttribute('data-counter-lit', '');
      if (!hover) this.release = setTimeout(() => this.extinguish(true), effect === 'red' ? 900 : 1600);
    }
    extinguish(instant = false) {
      clearTimeout(this.release);
      if (owner === this) owner = null;
      this.removeAttribute('data-counter-lit');
      const clear = () => { this.removeAttribute('data-counter-effect'); this.removeAttribute('data-counter-hovering'); };
      if (instant) clear();
      else this.release = setTimeout(clear, 350);
    }
    disconnectedCallback() {
      this.extinguish(true);
      this.lifecycle?.abort();
      this.visibility?.disconnect();
      counters.delete(this);
      if (!counters.size) clearTimeout(timer);
    }
  }
  customElements.define('cart-counter', CartCounter);
})();
